import { useCurrentUser } from '@/entities/auth';
import type {
  ApiAuthMeStaffPermissions,
} from '@/shared/types/api/auth';
import type { PermissionsTree } from '@/shared/types/api/roles';

/**
 * Internal: walk a dotted permission path through the nested permissions tree.
 * Returns true only when the final segment is reachable AND resolves to a
 * truthy value: literal `true`, OR a non-empty object (i.e. the user has at
 * least one sub-permission under this branch).
 *
 * Walking via intermediate object subtrees is allowed:
 *   `usePerm('parcels.list')` is true when the user has ANY column/filter
 *   permission under `parcels.list.*`.
 *
 * Returns false on any missing segment, on `null`/`undefined` input, on
 * non-object subtree (e.g. trying to walk under a leaf), or on empty object.
 */
const walk = (
  root: ApiAuthMeStaffPermissions | PermissionsTree | null,
  path: string,
): boolean => {
  if (!root || typeof root !== 'object') return false;
  const segments = path.split('.').filter(Boolean);
  if (segments.length === 0) return false;
  let cursor: true | PermissionsTree = root as PermissionsTree;
  for (const seg of segments) {
    if (cursor === true) return false; // can't descend into a leaf
    if (typeof cursor !== 'object') return false;
    const next: true | PermissionsTree | undefined = (cursor as PermissionsTree)[seg];
    if (next === undefined) return false;
    cursor = next;
  }
  if (cursor === true) return true;
  if (typeof cursor === 'object' && cursor !== null) {
    return Object.keys(cursor).length > 0;
  }
  return false;
};

/**
 * Pure helper for non-hook contexts (column builders that already pull perms
 * from the hook, route loaders, etc.). Honours the `__super__` shortcut.
 */
export const hasPerm = (
  perms: ApiAuthMeStaffPermissions | null | undefined,
  path: string,
): boolean => {
  if (!perms) return false;
  if (perms.__super__ === true) return true;
  return walk(perms, path);
};

/**
 * Resolve a single dotted permission path against the current staff user's
 * permissions tree exposed by `useCurrentUser()`.
 *
 * Returns true when the user is a super (`permissions.__super__ === true`),
 * or when walking the nested tree by dot-separated segments lands on a truthy
 * leaf/non-empty subtree. Returns false during loading, when unauthenticated,
 * when no role is attached, or when any segment is missing.
 */
export const usePerm = (path: string): boolean => {
  const { permissions } = useCurrentUser();
  return hasPerm(permissions, path);
};

/**
 * Expose the raw nested permissions object for advanced consumers (column
 * filters, dynamic forms). Returns null while loading / unauthenticated.
 */
export const usePermsRoot = (): ApiAuthMeStaffPermissions | null => {
  const { permissions } = useCurrentUser();
  return permissions;
};
